// src/pages/Dashboard/Dashboard.jsx
import React, { useMemo } from "react";

import "./Dashboard.css";

import useSafetyReports from "./hooks/useSafetyReports";
import useReportFilters from "./hooks/useReportFilters";

import {
  buildAvailableMonths,
  buildComparisonData,
  buildInjuryData,
  buildKpis,
  buildNonInjuryData,
  buildProjectList,
  buildTotals,
  groupMonthly,
} from "./utils/dataTransformers";

import FilterBar from "./components/FilterBar";
import DashboardHeader from "./components/DashboardHeader";
import KpiGrid from "./components/KpiGrid";
import MonthlyTrends from "./components/MonthlyTrends";
import ProjectComparison from "./components/ProjectComparison";

export default function Dashboard() {
  const { reports, loading, error } = useSafetyReports();

  const {
    projectId,
    setProjectId,
    monthYearValue,
    setMonthYear,
    comparisonMode,
    setComparisonMode,
    filteredReports,
  } = useReportFilters(reports);

  // Lists that depend on the full report set live here so the picker
  // options don't disappear when the user narrows the filters.
  const projects = useMemo(() => buildProjectList(reports), [reports]);
  const availableMonths = useMemo(
    () => buildAvailableMonths(reports),
    [reports],
  );

  // Everything below derives from the *filtered* reports.
  const monthlyData = useMemo(
    () => groupMonthly(filteredReports),
    [filteredReports],
  );
  const comparisonData = useMemo(
    () => buildComparisonData(filteredReports),
    [filteredReports],
  );
  const totals = useMemo(() => buildTotals(filteredReports), [filteredReports]);
  const kpis = useMemo(() => buildKpis(totals), [totals]);
  const injuryData = useMemo(
    () => buildInjuryData(filteredReports),
    [filteredReports],
  );
  const nonInjuryData = useMemo(
    () => buildNonInjuryData(monthlyData),
    [monthlyData],
  );

  const selectedProject = useMemo(
    () => projects.find((p) => p._id === projectId),
    [projects, projectId],
  );

  const numberFormatter = useMemo(
    () => new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 }),
    [],
  );

  if (loading) return <div className="sd-loading">Loading dashboard...</div>;
  if (error) return <div className="sd-error">{error}</div>;
  if (!filteredReports.length)
    return <div className="sd-empty">No data found.</div>;

  return (
    <div className="sd-container">
      <FilterBar
        projects={projects}
        projectId={projectId}
        onProjectChange={setProjectId}
        availableMonths={availableMonths}
        monthYearValue={monthYearValue}
        onMonthYearChange={setMonthYear}
        comparisonMode={comparisonMode}
        onComparisonModeChange={setComparisonMode}
      />

      <DashboardHeader selectedProject={selectedProject} />

      <KpiGrid kpis={kpis} formatter={numberFormatter} />

      <div className="sd-chart-grid">
        <MonthlyTrends
          monthlyData={monthlyData}
          injuryData={injuryData}
          nonInjuryData={nonInjuryData}
          formatter={numberFormatter}
        />
        <ProjectComparison comparisonData={comparisonData} />
      </div>
    </div>
  );
}
